/* eslint-disable no-param-reassign, no-nested-ternary */
import getQueryParam from 'Utils/getQueryParam';
import { reduceById } from 'Jsx/Catalog/utils';
import getBaseInitialState from 'Jsx/Utils/getBaseInitialState';

import transformKeys, {
  CATALOG_TRANSLATION_KEY,
} from 'Jsx/Utils/transformKeys';

import es from './locales/es';
import pt from './locales/pt';

const getLanguageItems = languageKey => {
  const spanishLanguageItem = {
    id: 1,
    slug: 'es',
    name: languageKey === 'pt' ? 'Espanhol' : 'Español',
  };

  const portugueseLanguageItem = {
    id: 2,
    slug: 'pt',
    name: languageKey === 'pt' ? 'Português' : 'Portugués',
  };

  return {
    spanishLanguageItem,
    portugueseLanguageItem,
  };
};

type languageItemType = {
  id: number;
  slug: string;
  name: string;
};

const getLanguageByQueryParam = (
  languageParams: string[],
  spanishLanguageItem: languageItemType,
  portugueseLanguageItem: languageItemType,
): any => {
  const selectedLanguages = {};

  languageParams.forEach(selectedLang => {
    if (selectedLang === 'pt') {
      selectedLanguages[portugueseLanguageItem.id] = portugueseLanguageItem;
    } else if (selectedLang === 'es') {
      selectedLanguages[spanishLanguageItem.id] = spanishLanguageItem;
    }
  });

  return selectedLanguages;
};

const getInitialSelectedLanguage = languageKey => {
  const languageParams = getQueryParam('idiomas');
  const { spanishLanguageItem, portugueseLanguageItem } = getLanguageItems(
    languageKey,
  );

  if (languageParams && languageParams.split(',').length > 0) {
    return getLanguageByQueryParam(
      languageParams.split(','),
      spanishLanguageItem,
      portugueseLanguageItem,
    );
  }

  return {};
};

const getInitialState = getBaseInitialState(
  (data, user, { LANGUAGE_KEY, translation_keys }) => {
    const state = {};

    // =============================== i18n ======================================= //
    const currentLocale = transformKeys(
      translation_keys,
      es,
      pt,
      CATALOG_TRANSLATION_KEY,
      LANGUAGE_KEY,
    );

    // =============================== initialize filters ========================= //

    const categorySelectId = data.filters.category
      ? `${data.filters.category.order}`
      : null;
    const subCategorySelectedId = data.filters.sub_category
      ? data.filters.sub_category.id
      : null;
    const levelsSelected = data.filters.levels.reduce(reduceById, {});
    const softwaresSelected = data.filters.softwares.reduce(reduceById, {});

    const allCourses = data.show_all || getQueryParam('all');
    const showRecommendedForMeCourses =
      data.recommeded_for_me || getQueryParam('recommended-for-me');

    const softwares = {
      id: 'software-filter',
      open: false,
      name: 'Software',
      subItems: data.softwares.reduce(reduceById, {}),
    };
    const levels = {
      id: 'level-filter',
      open: false,
      name: 'Niveles',
      subItems: data.levels.reduce(reduceById, {}),
    };

    const { spanishLanguageItem, portugueseLanguageItem } = getLanguageItems(
      LANGUAGE_KEY,
    );

    const languages = {
      id: 'language-filter',
      open: false,
      name: 'Idiomas',
      subItems: [spanishLanguageItem, portugueseLanguageItem].reduce(
        reduceById,
        {},
      ),
    };

    state.filters = {
      allCourses: !!allCourses,
      showRecommendedForMeCourses: !!showRecommendedForMeCourses,
      categorySelected: {
        itemId: categorySelectId,
        subItemId: subCategorySelectedId || (categorySelectId ? 0 : null),
      },
      categoryOpen: {
        itemId: categorySelectId,
      },
      levelsSelected,
      softwaresSelected,
      categories: data.categories
        .map(c => ({
          ...c,
          subItems: {
            0: { id: 0, name: 'Todos los cursos', slug: c.slug },
            ...c.sub_categories.reduce(reduceById, {}),
          },
        }))
        .reduce((obj, item) => {
          // created to order categories by keys
          const fakeId = `${item.order}`;

          obj[fakeId] = {
            ...item,
            originalId: item.id,
            id: fakeId,
          };

          return obj;
        }, {}),
      levels,
      languages,
      defaultLanguages: languages,
      languagesArray: [spanishLanguageItem, portugueseLanguageItem],
      selectedLanguages: getInitialSelectedLanguage(LANGUAGE_KEY),
      softwares,
      defaultSoftwares: softwares,
      defaultLevels: levels,
      openDialog: false,
    };

    const orderByItems = data.order_by.map(ob => ({
      ...ob,
      label: ob.name,
      value: ob.slug,
    }));

    const defaultOrderBy = orderByItems.find(o => o.slug === 'online');
    const orderBySelected = orderByItems.find(
      o => o.slug === data.filters.order_by.slug,
    );

    state.orderBy = {
      itemSelected: {
        ...orderBySelected,
        label: orderBySelected.name,
        value: orderBySelected.slug,
      },
      defaultOrderBy,
      items: orderByItems,
    };

    /*
      we don't need a reducer for nooResults, users, careers, etc
      we are not going to change this data
      don't forger to add a dummy reducer in the rootReducer file
    */

    state.search = {
      value: (data.search && data.search.value) || getQueryParam('q'),
      ...data.search,
    };
    state.tracks = data?.tracks?.items.edges.map(el => el.node) || [];
    state.user = user
      ? {
          isPremium: user.isPremiumMember,
          ...user,
        }
      : null;
    state.organization = data.organization;
    state.banners = data.banners;
    state.categoriesBanners = data.categories_banners;
    state.countryPrefix = data.country_prefix;
    state.userLanguage = LANGUAGE_KEY;

    state.translations = currentLocale.translations;
    state.currentLanguage = currentLocale.currentLanguage;

    return state;
  },
);

export default getInitialState;
